<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('external_coach_billing.title') }}
        </h6>

        <CreateButton
            :label="$t('general.create_invoice')"
            :to="{ name: 'coachBillingCoachingSelection' }">
        </CreateButton>
      </div>

      <div class="element-box">
        <Menu :countUnreadMessagesForAccountant="countUnreadMessagesForAccountant"/>

        <Search
            :filters="filters"
            :loading="isSearching"
            :coachDropdown="coachDropdown"
            :statusDropdown="statusDropdown"
            @search="getPendingCoachBills"/>

        <PerPage
            class="mt-3"
            :values="[10, 25, 50, 100]"
            @update:perPage="getPendingCoachBills"/>

        <EmptyState
            v-if="!coachBills.data?.length"
            icon="fa-file-invoice"
            title="Keine Rechnungen gefunden"
            subtitle="Es wurden keine Rechnungen gefunden, die Ihren Suchkriterien entsprechen."/>

        <form v-else @submit.prevent="" method="post">
          <div class="table-responsive overflow-auto pt-4">
            <table class="table table-striped">
              <thead>
              <tr>
                <th style="width: 3%;"></th>
                <th>Rechnungs-Nr.</th>
                <th style="width: 20%;">Coach</th>
                <th>Kreditor</th>
                <th>Rechnungsdatum</th>
                <th class="text-end"># Sitzungen</th>
                <th class="text-end">Rechnungsbetrag</th>
                <th class="text-start"><span class="ps-5">Status</span></th>
                <th style="width: 5%;"></th>
                <th style="width: 5%;"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(coachBill) in coachBills.data" :key="coachBill.id">
                <td class="text-center">
                  <label class="custom-checkbox position-relative">
                    <input type="checkbox" v-model="selectedBillIds" :value="coachBill.id"
                           class="custom-checkbox position-absolute"/>
                    <div class="custom-check-icon"></div>
                  </label>
                </td>
                <td>GS {{ coachBill?.billNumber }}</td>
                <td>{{ coachBill?.coach?.firstName }} {{ coachBill?.coach?.lastName }}</td>
                <td>{{ coachBill?.coach?.creditor }}</td>
                <td>{{ coachBill.createdAt }}</td>
                <td class="text-end">
                  <span v-if="coachBill.billingType === 'pauschalbetrag'">
                        Pauschalbetrag
                  </span>
                  <span v-else>
                    {{ coachBill.totalUnits }}
                  </span>
                </td>
                <td class="text-end">{{ coachBill.sum }}</td>
                <td class="text-start" style="min-width: 160px">
                  <div class="ps-3">
                    <div v-if="coachBill.status === 'billed' || coachBill.status === 'approved'"
                         class="status-pill green p-2 me-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    <div v-else-if="coachBill.status === 'pending'"
                         class="status-pill yellow p-2 me-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    <div v-else
                         class="status-pill red p-2 me-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    {{ coachBill.statusTranslation }}
                  </div>
                </td>
                <td class="text-center">
                  <div v-if="coachBill.status === 'rejected' || coachBill.status === 'answered'">
                    <router-link :to="{ name: 'coachBillingPendingBillsChat', params: { id: coachBill.id } }">
                      <i style="font-size: 26px; color: #43ad4e;" class="os-icon os-icon-mail-07"></i>
                      <span v-if="coachBill.messageForAccountant"
                            style="
                              position: absolute;
                              font-size: 8px;
                              padding: 3px 8px;
                              background-color: #DC3545FF;
                              color: white;
                              border-radius: 11px;">!</span>
                    </router-link>

                  </div>
                </td>
                <td class="text-end">
                  <Buttons :coachBill="coachBill"
                           @rejectBill="rejectBillModal"
                           @createBill="createBillModal"
                           @showReasonForRejection="reasonForRejectionModal"/>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div class="row d-flex align-items-start mt-2">
            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-start mt-4">
              <DownloadButton
                  :label="'Rechnung download'"
                  :loading="isBillDownloaded"
                  @click="downloadBills"/>
            </div>

            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-end">
              <Pagination
                  v-if="coachBills?.pagination?.total_pages > 1"
                  :totalPages="coachBills?.pagination?.total_pages"
                  :currentPage="coachBills?.pagination?.current_page"
                  :totalItems="coachBills?.pagination?.total_items"
                  :perPage="coachBills?.pagination?.items_per_page"
                  @pagechanged="getPendingCoachBills"/>
            </div>
          </div>
        </form>

      </div>
    </div>

    <Modal
        v-model="showRejectBillModal"
        title="Rechnung ablehnen"
        :hide-cancel="true"
        :button-label="$t('general.button.save')"
        sizeModal="modal-lg"
        :hide-footer="true"
        classFooter="justify-content-center">
      <RejectBillForm
          :coachBillId="coachBillId"
          v-model:submitForm="submitForm"/>
    </Modal>

    <Modal
        v-model="showReasonForRejectionModal"
        title="Grund für die Ablehnung"
        :hide-cancel="true"
        sizeModal="modal-lg"
        :hide-footer="true"
        classFooter="justify-content-center">
      <ShowReasonForRejection :reasonForRejection="reasonForRejection"/>
    </Modal>

    <ModalConfirm
        v-model="showCreateBillModal"
        :confirmationQuestion="t('general.approve_question')"
        header=""
        :onConfirm="createBillAfterApproving"/>

  </PageTemplate>
</template>


<script setup>
import coachBillingService from '@/api/CoachBilling';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {getExternalCoachForBillingDropdown} from '@/shared/dropdowns';
import {downloadFile} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Menu from '@/views/App/CoachBilling/Accountant/Menu.vue';
import Buttons from '@/views/App/CoachBilling/Accountant/Pending/Buttons.vue';
import RejectBillForm from '@/views/App/CoachBilling/Accountant/Pending/RejectBillForm.vue';
import Search from '@/views/App/CoachBilling/Accountant/Pending/Search.vue';
import ShowReasonForRejection from '@/views/App/CoachBilling/Accountant/Pending/ShowReasonForRejection.vue';
import {computed, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import { useRoute } from 'vue-router';
import DownloadButton from "@/components/button/DownloadButton.vue";

const route = useRoute();
const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const {t} = useI18n();
const coachBills = ref([]);
const coachDropdown = ref([]);
const statusDropdown = ref([]);
const selectedBillIds = ref([]);
const isSearching = ref(false);
const isLoaded = ref(false);
const showRejectBillModal = ref(false);
const showCreateBillModal = ref(false);
const showReasonForRejectionModal = ref(false);
const coachBillId = ref('');
const reasonForRejection = ref('');
const submitForm = ref(false);
const isBillDownloaded = ref(false)
const countUnreadMessagesForAccountant = ref(0);


const filters = ref({
  coachId: '',
  billNumber: '',
  startDate: '',
  endDate: '',
  withMessage: false,
});

watch(submitForm, () => {
  coachBillId.value = '';
  showRejectBillModal.value = false;
  showCreateBillModal.value = false;
  showReasonForRejectionModal.value = false;
  getPendingCoachBills();
});

async function getPendingCoachBills(page = 1, perPage, passedFilters) {

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachBills.value?.pagination?.items_per_page;

  try {
    const {data} = await coachBillingService.getPendingBillsList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachBills.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function createBillAfterApproving() {
  CloseToast();
  try {
    await coachBillingService.createBillAfterApproving(coachBillId.value);

    coachBillId.value = '';
    showCreateBillModal.value = false;

    await router.push({name: 'coachBillingExistedBills'});
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function rejectBillModal(id) {
  coachBillId.value = '';
  showRejectBillModal.value = true;
  showCreateBillModal.value = false;
  showReasonForRejectionModal.value = false;
  coachBillId.value = id;
  CloseToast();
}

function createBillModal(id) {
  coachBillId.value = '';
  showCreateBillModal.value = true;
  showRejectBillModal.value = false;
  showReasonForRejectionModal.value = false;
  coachBillId.value = id;
  CloseToast();
}

async function reasonForRejectionModal(id) {
  coachBillId.value = '';
  reasonForRejection.value = '';
  showReasonForRejectionModal.value = true;
  showCreateBillModal.value = false;
  showRejectBillModal.value = false;
  coachBillId.value = id;
  CloseToast();

  try {
    const {data} = await coachBillingService.showReasonForRejectionOfPendingBill(coachBillId.value);
    reasonForRejection.value = data.data;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function downloadBills() {
  CloseToast();

  try {
    if (selectedBillIds.value.length === 0) {
      await ErrorToast({
        response: {
          data: {
            error: {
              message: 'Rechnung zum Herunterladen auswählen!'
            }
          }
        }
      });
    } else {
      await downloadFile('/coach-billing/download-bills',
          undefined,
          {billIds: selectedBillIds.value},
          false, 'POST',
          isBillDownloaded
      );
      selectedBillIds.value = [];
    }

  } catch (error) {
    await ErrorToast(error);
  }
}

async function countUnreadMessages() {
  try {
    const {data} = await coachBillingService.countUnreadMessagesForAccountant();
    countUnreadMessagesForAccountant.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getStatusDropdown() {
  //statusDropdown.value = getEnums?.value?.billStatuses.filter(status => status.value === 'billed' || status.value === 'canceled') || [];
  statusDropdown.value = getEnums?.value?.billStatuses;
}

onMounted(async () => {
  if (route.query.withMessage === '1') {
    filters.value.withMessage = true;
  }

  await Promise.all([
    getPendingCoachBills(),
    getExternalCoachForBillingDropdown(coachDropdown),
    countUnreadMessages(),
    getStatusDropdown(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>
.custom-checkbox .custom-check-icon {
  position: relative;
  display: inline-block;
  width: 18px;
  min-width: 18px;
  height: 18px;
  min-height: 18px;
  border: 2px solid var(--primary);
  border-radius: 4px;
  background-color: white;
  margin-right: 8px;
}

.custom-checkbox .custom-check-icon::before {
  content: '';
  position: absolute;
  top: 1px;
  left: 4px;
  width: 6px;
  height: 11px;
  border: solid var(--primary);
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.custom-checkbox input[type="checkbox"]:checked + .custom-check-icon::before {
  opacity: 1;
}

.custom-checkbox .custom-check-icon:hover {
  border-color: var(--primary);
}
</style>